import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CARE_PLAN_ACTIVITIES_AT_A_GLANCE, CarePlanSectionProps, INSTRUMENTAL_ACTIVITY_OPTIONS, NA, NO, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, YES } from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE, SUGGESTED_INSTRUCTIONS_SMOKING } from "../../../constants/carePlanConstants";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  InstrumentalActivitiesGenieSectionValues,
  IADL_GENIE_FIELD_DEFINITIONS,
  IADL_GENIE_QUESTIONS,
} from "./instrumentalActivitiesGenie";
import SuggestedInstructionsDialog from "./suggestedInstructionsDialog";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const InstrumentalActivitiesSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);

  const toast = useRef<Toast>(null);
  const [showSuggestedInstructionsDialog, setShowSuggestedInstructionsDialog] = useState<boolean>(false);
  const [suggestedInstructions, setSuggestedInstructions] = useState<any[]>([]);
  const [instructionsFieldName, setInstructionsFieldName] = useState<string>("");

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.INSTRUMENTAL_ACTIVITIES_OF_DAILY_LIVING,

    // Managing Finances
    manageFinanceLevel: sectionData?.manageFinanceLevel || '',
    manageFinanceDetails: sectionData?.manageFinanceDetails || '',
    manageFinanceAssistanceRequired: sectionData?.manageFinanceAssistanceRequired || '',

    anyWhoManagesFinance: sectionData?.anyWhoManagesFinance || false,
    anyWhoManagesFinanceDetails: sectionData?.anyWhoManagesFinanceDetails || '',
    anyWhoManagesFinanceAssistanceRequired: sectionData?.anyWhoManagesFinanceAssistanceRequired || '',

    anyWhoManagesFinancialRecords: sectionData?.anyWhoManagesFinancialRecords || false,
    anyWhoManagesFinancialRecordsDetails: sectionData?.anyWhoManagesFinancialRecordsDetails || '',
    anyWhoManagesFinancialRecordsAssistanceRequired: sectionData?.anyWhoManagesFinancialRecordsAssistanceRequired || '',

    anyPayeeDetails: sectionData?.anyPayeeDetails || false,
    payeeDetails: sectionData?.payeeDetails || '',

    // Shopping
    manageShoppingLevel: sectionData?.manageShoppingLevel || '',
    manageShoppingDetails: sectionData?.manageShoppingDetails || '',
    manageShoppingAssistanceRequired: sectionData?.manageShoppingAssistanceRequired || '',

    specialShoppingTransportationNeeds: sectionData?.specialShoppingTransportationNeeds || false,
    specialShoppingTransportationDetails: sectionData?.specialShoppingTransportationDetails || '',
    specialShoppingTransportationAssistanceRequired: sectionData?.specialShoppingTransportationAssistanceRequired || '',

    shoppingFrequencyDetails: sectionData?.shoppingFrequencyDetails || '',
    shoppingFrequencyAssistanceRequired: sectionData?.shoppingFrequencyAssistanceRequired || '',

    anyShoppingEquipmentNeeds: sectionData?.anyShoppingEquipmentNeeds || false,
    shoppingEquipmentDetails: sectionData?.shoppingEquipmentDetails || '',
    shoppingEquipmentAssistanceRequired: sectionData?.shoppingEquipmentAssistanceRequired || '',

    anyShoppingLimitations: sectionData?.anyShoppingLimitations || false,
    shoppingLimitationsDetails: sectionData?.shoppingLimitationsDetails || '',
    shoppingLimitationsAssistanceRequired: sectionData?.shoppingLimitationsAssistanceRequired || '',

    // Transportation
    transportationManageLevel: sectionData?.transportationManageLevel || '',
    transportationManageDetails: sectionData?.transportationManageDetails || '',
    transportationManageAssistanceRequired: sectionData?.transportationManageAssistanceRequired || '',

    specialTransportationNeeds: sectionData?.specialTransportationNeeds || false,
    specialTransportationDetails: sectionData?.specialTransportationDetails || '',
    specialTransportationAssistanceRequired: sectionData?.specialTransportationAssistanceRequired || '',

    anyTransportationMedicalNeeds: sectionData?.anyTransportationMedicalNeeds || false,
    transportationMedicalDetails: sectionData?.transportationMedicalDetails || '',
    transportationMedicalAssistanceRequired: sectionData?.transportationMedicalAssistanceRequired || '',

    anyTransportationEscortNeeds: sectionData?.anyTransportationEscortNeeds || false,
    transportationEscortDetails: sectionData?.transportationEscortDetails || '',
    transportationEscortAssistanceRequired: sectionData?.transportationEscortAssistanceRequired || '',

    transportationFrequencyDetails: sectionData?.transportationFrequencyDetails || '',
    transportationFrequencyAssistanceRequired: sectionData?.transportationFrequencyAssistanceRequired || '',

    anyTransportationEquipmentNeeds: sectionData?.anyTransportationEquipmentNeeds || false,
    transportationEquipmentDetails: sectionData?.transportationEquipmentDetails || '',
    transportationEquipmentAssistanceRequired: sectionData?.transportationEquipmentAssistanceRequired || '',

    anyTransportationLimitations: sectionData?.anyTransportationLimitations || false,
    transportationLimitationsDetails: sectionData?.transportationLimitationsDetails || '',
    transportationLimitationsAssistanceRequired: sectionData?.transportationLimitationsAssistanceRequired || '',

    // Activity social engagement
    activityLevel: sectionData?.activityLevel || '',
    activityDetails: sectionData?.activityDetails || '',
    activityAssistanceRequired: sectionData?.activityAssistanceRequired || '',

    anyReligiousActivities: sectionData?.anyReligiousActivities || false,
    religiousActivitiesDetails: sectionData?.religiousActivitiesDetails || '',
    religiousActivitiesAssistanceRequired: sectionData?.religiousActivitiesAssistanceRequired || '',

    anySocialCulturalActivities: sectionData?.anySocialCulturalActivities || false,
    socialCulturalActivitiesDetails: sectionData?.socialCulturalActivitiesDetails || '',
    socialCulturalActivitiesAssistanceRequired: sectionData?.socialCulturalActivitiesAssistanceRequired || '',

    anyFamilyFriendsRelationships: sectionData?.anyFamilyFriendsRelationships || false,
    familyFriendsRelationshipsDetails: sectionData?.familyFriendsRelationshipsDetails || '',
    familyFriendsRelationshipsAssistanceRequired: sectionData?.familyFriendsRelationshipsAssistanceRequired || '',

    anyEmploymentSupport: sectionData?.anyEmploymentSupport || false,
    employmentSupportDetails: sectionData?.employmentSupportDetails || '',
    employmentSupportAssistanceRequired: sectionData?.employmentSupportAssistanceRequired || '',

    anyClubsGroups: sectionData?.anyClubsGroups || false,
    clubsGroupsDetails: sectionData?.clubsGroupsDetails || '',
    clubsGroupsAssistanceRequired: sectionData?.clubsGroupsAssistanceRequired || '',

    anySpecialArrangements: sectionData?.anySpecialArrangements || false,
    specialArrangementsDetails: sectionData?.specialArrangementsDetails || '',
    specialArrangementsAssistanceRequired: sectionData?.specialArrangementsAssistanceRequired || '',

    anyParticipationIssues: sectionData?.anyParticipationIssues || false,
    participationIssuesDetails: sectionData?.participationIssuesDetails || '',
    participationIssuesAssistanceRequired: sectionData?.participationIssuesAssistanceRequired || '',

    emergencyNumbers: sectionData?.emergencyNumbers || '',

    activitiesAtGlance: sectionData?.activitiesAtGlance || [],

    anyOtherActivities: sectionData?.anyOtherActivities || false,
    otherActivitiesDetails: sectionData?.otherActivitiesDetails || '',

    activityNarrative: sectionData?.activityNarrative || '',

    // Smoking
    doesSmoke: sectionData?.doesSmoke || false,
    smokingDetails: sectionData?.smokingDetails || '',
    smokingAssistanceRequired: sectionData?.smokingAssistanceRequired || '',

    anySmokingSafetyConcerns: sectionData?.anySmokingSafetyConcerns || false,
    smokingSafetyConcernsDetails: sectionData?.smokingSafetyConcernsDetails || '',
    smokingSafetyConcernsAssistanceRequired: sectionData?.smokingSafetyConcernsAssistanceRequired || '',

    anySmokingPolicySigned: sectionData?.anySmokingPolicySigned || false,
    smokingPolicySignedDetails: sectionData?.smokingPolicySignedDetails || '',

    smokingStorageDetails: sectionData?.smokingStorageDetails || '',

    // Case management
    receivesCaseManagement: sectionData?.receivesCaseManagement || false,
    caseManagementDetails: sectionData?.caseManagementDetails || '',
    caseManagementAssistanceRequired: sectionData?.caseManagementAssistanceRequired || '',

    conditionsWhenToContactCaseManager: sectionData?.conditionsWhenToContactCaseManager || '',

    caseManagerContactName: sectionData?.caseManagerContactName || '',
    caseManagerContactAgency: sectionData?.caseManagerContactAgency || '',
    caseManagerContactPhone: sectionData?.caseManagerContactPhone || '',
    caseManagerContactEmail: sectionData?.caseManagerContactEmail || '',
    caseManagerContactFax: sectionData?.caseManagerContactFax || '',

    // Other information
    additionalInformation: sectionData?.additionalInformation || '',
    additionalInformationAssistanceRequired: sectionData?.additionalInformationAssistanceRequired || '',

    ncpReviewDetails: sectionData?.ncpReviewDetails || '',
    ncpReviewAssistanceRequired: sectionData?.ncpReviewAssistanceRequired || '',
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, setValue, getValues } = useForm({ defaultValues });
  const watchAnyWhoManagesFinance = watch('anyWhoManagesFinance', defaultValues.anyWhoManagesFinance);
  const watchAnyPayeeDetails = watch('anyPayeeDetails', defaultValues.anyPayeeDetails);
  const watchAnyWhoManagesFinancialRecords = watch('anyWhoManagesFinancialRecords', defaultValues.anyWhoManagesFinancialRecords);

  const watchAnyShoppingEquipmentNeeds = watch('anyShoppingEquipmentNeeds', defaultValues.anyShoppingEquipmentNeeds);
  const watchAnyShoppingLimitations = watch('anyShoppingLimitations', defaultValues.anyShoppingLimitations);
  const watchSpecialShoppingTransportationNeeds = watch('specialShoppingTransportationNeeds', defaultValues.specialShoppingTransportationNeeds);

  const watchAnyTransportationEquipmentNeeds = watch('anyTransportationEquipmentNeeds', defaultValues.anyTransportationEquipmentNeeds);
  const watchAnyTransportationLimitations = watch('anyTransportationLimitations', defaultValues.anyTransportationLimitations);
  const watchAnyTransportationMedicalNeeds = watch('anyTransportationMedicalNeeds', defaultValues.anyTransportationMedicalNeeds);
  const watchAnyTransportationEscortNeeds = watch('anyTransportationEscortNeeds', defaultValues.anyTransportationEscortNeeds);
  const watchSpecialTransportationNeeds = watch('specialTransportationNeeds', defaultValues.specialTransportationNeeds);

  const watchAnyReligiousActivities = watch('anyReligiousActivities', defaultValues.anyReligiousActivities);
  const watchAnySocialCulturalActivities = watch('anySocialCulturalActivities', defaultValues.anySocialCulturalActivities);
  const watchAnyFamilyFriendsRelationships = watch('anyFamilyFriendsRelationships', defaultValues.anyFamilyFriendsRelationships);
  const watchAnyEmploymentSupport = watch('anyEmploymentSupport', defaultValues.anyEmploymentSupport);
  const watchAnyClubsGroups = watch('anyClubsGroups', defaultValues.anyClubsGroups);
  const watchAnySpecialArrangements = watch('anySpecialArrangements', defaultValues.anySpecialArrangements);
  const watchAnyParticipationIssues = watch('anyParticipationIssues', defaultValues.anyParticipationIssues);

  const watchAnyOtherActivities = watch('anyOtherActivities', defaultValues.anyOtherActivities);

  const watchDoesSmoke = watch('doesSmoke', defaultValues.doesSmoke);
  const watchAnySmokingSafetyConcerns = watch('anySmokingSafetyConcerns', defaultValues.anySmokingSafetyConcerns);
  const watchAnySmokingPolicySigned = watch('anySmokingPolicySigned', defaultValues.anySmokingPolicySigned);

  const watchReceivesCaseManagement = watch('receivesCaseManagement', defaultValues.receivesCaseManagement);


  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
      </div>
    )
  };

  const renderSuggestedInstructionsDialog = (e, fieldName, instructions) => {
    e.preventDefault();
    setSuggestedInstructions(instructions);
    setInstructionsFieldName(fieldName);
    setShowSuggestedInstructionsDialog(true);
  }

  const onSaveSuggestedInstructions = (data) => {
    setShowSuggestedInstructionsDialog(false);
    let existingInstructions = getValues(data.fieldName) || "";
    setValue(data.fieldName, existingInstructions + "\n" + data.instructions, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={IADL_GENIE_QUESTIONS}
        fieldDefinitions={IADL_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as InstrumentalActivitiesGenieSectionValues}
        successMessage="Instrumental activities of daily living care plan updated."
        errorMessage="Unable to update the instrumental activities of daily living care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = { ...getValues(), ...payload.valueUpdates };
          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });
          await onsubmit(nextValues);
        }}
      />

      <SuggestedInstructionsDialog showDialog={showSuggestedInstructionsDialog}
        fieldName={instructionsFieldName}
        submitButtonClick={(data) => { onSaveSuggestedInstructions(data); }}
        onClose={() => setShowSuggestedInstructionsDialog(false)}
        suggestedInstructionsData={suggestedInstructions} />

      <Card className="resident-careplan-card" footer={actions()} header={actions()} >
        <div className="">
          <form className="">
            <Accordion multiple>
              <AccordionTab header="Managing Finances">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <div className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_FINANCE_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </div>
                    <div className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.IADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      IADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </div>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_FINANCE_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <Divider><Tag severity="warning">Finance</Tag></Divider>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.manageFinanceLevel })}>Resident finance management stage:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="manageFinanceLevel"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={INSTRUMENTAL_ACTIVITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select an option"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('manageFinanceLevel')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="manageFinanceDetails" className={classNames({ 'p-error': errors.manageFinanceDetails })}>
                              Add finance details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="manageFinanceDetails" control={control}
                              rules={{ required: 'Finance details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add finance details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('manageFinanceDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="manageFinanceAssistanceRequired" className={classNames({ 'p-error': errors.manageFinanceAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="manageFinanceAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for finance management"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('manageFinanceAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident finance management stage: </strong>{sectionData?.manageFinanceLevel}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Finance details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.manageFinanceDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with finance management: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.manageFinanceAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyWhoManagesFinance })}>Add details who manages finance?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyWhoManagesFinance"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyWhoManagesFinance')}
                        </div>

                        {
                          watchAnyWhoManagesFinance && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="anyWhoManagesFinanceDetails" className={classNames({ 'p-error': errors.anyWhoManagesFinanceDetails })}>
                                    Who manages the finance? Add details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="anyWhoManagesFinanceDetails" control={control}
                                    rules={{ required: 'Finance details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add finance details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('anyWhoManagesFinanceDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="anyWhoManagesFinanceAssistanceRequired" className={classNames({ 'p-error': errors.anyWhoManagesFinanceAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="anyWhoManagesFinanceAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for finance management"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('anyWhoManagesFinanceAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Who manages finance details included? </strong>{sectionData?.anyWhoManagesFinance ? YES : NO}</div>
                        {
                          sectionData?.anyWhoManagesFinance && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Who manages finance: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyWhoManagesFinanceDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with finance management: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyWhoManagesFinanceAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyWhoManagesFinancialRecords })}>Add details who manages financial records?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyWhoManagesFinancialRecords"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyWhoManagesFinancialRecords')}
                        </div>

                        {
                          watchAnyWhoManagesFinancialRecords && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="anyWhoManagesFinancialRecordsDetails" className={classNames({ 'p-error': errors.anyWhoManagesFinancialRecordsDetails })}>
                                    Who manages the financial records? Add details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="anyWhoManagesFinancialRecordsDetails" control={control}
                                    rules={{ required: 'Finance details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add finance details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('anyWhoManagesFinancialRecordsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="anyWhoManagesFinancialRecordsAssistanceRequired" className={classNames({ 'p-error': errors.anyWhoManagesFinancialRecordsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="anyWhoManagesFinancialRecordsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for finance management"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('anyWhoManagesFinancialRecordsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Who manages financial records details included? </strong>{sectionData?.anyWhoManagesFinancialRecords ? YES : NO}</div>
                        {
                          sectionData?.anyWhoManagesFinancialRecords && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Who manages financial records: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyWhoManagesFinancialRecordsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with records management: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anyWhoManagesFinancialRecordsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyPayeeDetails })}>Add payee details?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyPayeeDetails"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyPayeeDetails')}
                        </div>

                        {
                          watchAnyPayeeDetails && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="payeeDetails" className={classNames({ 'p-error': errors.payeeDetails })}>
                                    Add payee name and contact details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="payeeDetails" control={control}
                                    rules={{ required: 'Payee details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add payee details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('payeeDetails')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any payee details included? </strong>{sectionData?.anyPayeeDetails ? YES : NO}</div>
                        {
                          sectionData?.payeeDetails && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Payee details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.payeeDetails || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Shopping">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <div className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_SHOPPING_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </div>
                    <div className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.IADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      IADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </div>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_SHOPPING_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <Divider><Tag severity="warning">Shopping</Tag></Divider>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.manageShoppingLevel })}>Resident shopping stage:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="manageShoppingLevel"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={INSTRUMENTAL_ACTIVITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select an option"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('manageShoppingLevel')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="manageShoppingDetails" className={classNames({ 'p-error': errors.manageShoppingDetails })}>
                              Add shopping details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="manageShoppingDetails" control={control}
                              rules={{ required: 'Shopping details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add shopping details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('manageShoppingDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="manageShoppingAssistanceRequired" className={classNames({ 'p-error': errors.manageShoppingAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="manageShoppingAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for shopping"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('manageShoppingAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any shopping management? </strong>{sectionData?.manageShoppingLevel}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Shopping details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.manageShoppingDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with shopping management: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.manageShoppingAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.specialShoppingTransportationNeeds })}>Any special transportation needs?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="specialShoppingTransportationNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('specialShoppingTransportationNeeds')}
                        </div>

                        {
                          watchSpecialShoppingTransportationNeeds && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialShoppingTransportationNeedsDetails" className={classNames({ 'p-error': errors.specialShoppingTransportationDetails })}>
                                    Add details of special transportation needs.
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialShoppingTransportationDetails" control={control}
                                    rules={{ required: 'Transportation details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add transportation details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialShoppingTransportationDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialShoppingTransportationAssistanceRequired" className={classNames({ 'p-error': errors.specialShoppingTransportationAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialShoppingTransportationAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for transportation"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialShoppingTransportationAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special transportation needs? </strong>{sectionData?.specialShoppingTransportationNeeds ? YES : NO}</div>
                        {
                          sectionData?.specialShoppingTransportationNeeds && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Transportation details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialShoppingTransportationDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialShoppingTransportationAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="shoppingFrequencyDetails" className={classNames({ 'p-error': errors.shoppingFrequencyDetails })}>
                              How often does the resident go shopping?:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="shoppingFrequencyDetails" control={control}
                              rules={{ required: 'Shopping frequency details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add shopping frequency details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('shoppingFrequencyDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="shoppingFrequencyAssistanceRequired" className={classNames({ 'p-error': errors.shoppingFrequencyAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="shoppingFrequencyAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for transportation"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('shoppingFrequencyAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Shopping frequency details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shoppingFrequencyDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with shopping: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shoppingFrequencyAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyShoppingEquipmentNeeds })}>Any special shopping equipment needs?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyShoppingEquipmentNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyShoppingEquipmentNeeds')}
                        </div>

                        {
                          watchAnyShoppingEquipmentNeeds && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="shoppingEquipmentDetails" className={classNames({ 'p-error': errors.shoppingEquipmentDetails })}>
                                    Equipments/Supplies/Procedures:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="shoppingEquipmentDetails" control={control}
                                    rules={{ required: 'Shopping equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add shopping equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('shoppingEquipmentDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="shoppingEquipmentAssistanceRequired" className={classNames({ 'p-error': errors.shoppingEquipmentAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="shoppingEquipmentAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for shopping equipment"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('shoppingEquipmentAssistanceRequired')}
                              </div>
                            </>)
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special shopping equipment needs? </strong>{sectionData?.anyShoppingEquipmentNeeds ? YES : NO}</div>
                        {
                          sectionData?.anyShoppingEquipmentNeeds && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Shopping equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shoppingEquipmentDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with shopping: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shoppingEquipmentAssistanceRequired || NA)}</span>
                              </div>
                            </>)
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyShoppingLimitations })}>Any shopping limitations?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyShoppingLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyShoppingLimitations')}
                        </div>

                        {
                          watchAnyShoppingLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="shoppingLimitationsDetails" className={classNames({ 'p-error': errors.shoppingLimitationsDetails })}>
                                    Add details of shopping limitations:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="shoppingLimitationsDetails" control={control}
                                    rules={{ required: 'Shopping limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add shopping limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('shoppingLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="shoppingLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.shoppingLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="shoppingLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for shopping limitations"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('shoppingLimitationsAssistanceRequired')}
                              </div>
                            </>)
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any shopping limitations? </strong>{sectionData?.anyShoppingLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyShoppingLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Shopping limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shoppingLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with shopping limitations: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shoppingLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>)
                        }
                      </div>
                    )
                  }
                </div>

              </AccordionTab>
              <AccordionTab header="Transportation">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_TRANSPORTATION_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.IADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      IADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_TRANSPORTATION_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <Divider><Tag severity="warning">Transportation</Tag></Divider>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.transportationManageLevel })}>Resident transportation stage:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="transportationManageLevel"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={INSTRUMENTAL_ACTIVITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select an option"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('transportationManageLevel')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="transportationManageDetails" className={classNames({ 'p-error': errors.transportationManageDetails })}>
                              Add transportation details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="transportationManageDetails" control={control}
                              rules={{ required: 'Transportation details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add transportation details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('transportationManageDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="transportationManageAssistanceRequired" className={classNames({ 'p-error': errors.transportationManageAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="transportationManageAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for transportation"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('transportationManageAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident transportation management level: </strong>{sectionData?.transportationManageLevel}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Transportation details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationManageDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation management: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationManageAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyTransportationMedicalNeeds })}>Any transportation medical needs?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyTransportationMedicalNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyTransportationMedicalNeeds')}
                        </div>

                        {
                          watchAnyTransportationMedicalNeeds && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationMedicalDetails" className={classNames({ 'p-error': errors.transportationMedicalDetails })}>
                                    Add details of transportation medical needs:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationMedicalDetails" control={control}
                                    rules={{ required: 'Transportation details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add transportation details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationMedicalDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationMedicalAssistanceRequired" className={classNames({ 'p-error': errors.transportationMedicalAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationMedicalAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for transportation"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationMedicalAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any transportation medical needs? </strong>{sectionData?.anyTransportationMedicalNeeds ? YES : NO}</div>
                        {
                          sectionData?.anyTransportationMedicalNeeds && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Transportation details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationMedicalDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationMedicalAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.specialTransportationNeeds })}>Any special transportation needs?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="specialTransportationNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('specialTransportationNeeds')}
                        </div>

                        {
                          watchSpecialTransportationNeeds && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialTransportationDetails" className={classNames({ 'p-error': errors.specialTransportationDetails })}>
                                    Add details of special transportation needs. Special transportation needs may include using a lift van, assistive devices to help get in or out of the car, seatbelt extension, etc.
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialTransportationDetails" control={control}
                                    rules={{ required: 'Transportation details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add transportation details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialTransportationDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialTransportationAssistanceRequired" className={classNames({ 'p-error': errors.specialTransportationAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialTransportationAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for transportation"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialTransportationAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special transportation needs? </strong>{sectionData?.specialTransportationNeeds ? YES : NO}</div>
                        {
                          sectionData?.specialTransportationNeeds && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Special transportation details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialTransportationDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialTransportationAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyTransportationEscortNeeds })}>Any transportation escort needs?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyTransportationEscortNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyTransportationEscortNeeds')}
                        </div>

                        {
                          watchAnyTransportationEscortNeeds && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationEscortDetails" className={classNames({ 'p-error': errors.transportationEscortDetails })}>
                                    Add details of transportation escort:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationEscortDetails" control={control}
                                    rules={{ required: 'Transportation escort details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add transportation escort details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationEscortDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationEscortAssistanceRequired" className={classNames({ 'p-error': errors.transportationEscortAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationEscortAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for transportation escort"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationEscortAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any transportation escort needs? </strong>{sectionData?.anyTransportationEscortNeeds ? YES : NO}</div>
                        {
                          sectionData?.anyTransportationEscortNeeds && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Transportation escort details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationEscortDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation escort: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationEscortAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="transportationFrequencyDetails" className={classNames({ 'p-error': errors.transportationFrequencyDetails })}>
                              How often does the resident require transportation?:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="transportationFrequencyDetails" control={control}
                              rules={{ required: 'Transportation frequency details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add transportation frequency details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('transportationFrequencyDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="transportationFrequencyAssistanceRequired" className={classNames({ 'p-error': errors.transportationFrequencyAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="transportationFrequencyAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for transportation"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('transportationFrequencyAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Transportation frequency details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationFrequencyDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationFrequencyAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyTransportationEquipmentNeeds })}>Any special transportation equipment needs?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyTransportationEquipmentNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyTransportationEquipmentNeeds')}
                        </div>

                        {
                          watchAnyTransportationEquipmentNeeds && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationEquipmentDetails" className={classNames({ 'p-error': errors.transportationEquipmentDetails })}>
                                    Equipments/Supplies/Procedures:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationEquipmentDetails" control={control}
                                    rules={{ required: 'Transportation equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add transportation equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationEquipmentDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationEquipmentAssistanceRequired" className={classNames({ 'p-error': errors.transportationEquipmentAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationEquipmentAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for transportation equipment"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationEquipmentAssistanceRequired')}
                              </div>
                            </>)
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special transportation equipment needs? </strong>{sectionData?.anyTransportationEquipmentNeeds ? YES : NO}</div>
                        {
                          sectionData?.anyTransportationEquipmentNeeds && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Transportation equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationEquipmentDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation equipment: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationEquipmentAssistanceRequired || NA)}</span>
                              </div>
                            </>)
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyTransportationLimitations })}>Any transportation limitations?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyTransportationLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyTransportationLimitations')}
                        </div>

                        {
                          watchAnyTransportationLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationLimitationsDetails" className={classNames({ 'p-error': errors.transportationLimitationsDetails })}>
                                    Add details of transportation limitations:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationLimitationsDetails" control={control}
                                    rules={{ required: 'Transportation limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add transportation limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="transportationLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.transportationLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="transportationLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for transportation limitations"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('transportationLimitationsAssistanceRequired')}
                              </div>
                            </>)
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any transportation limitations? </strong>{sectionData?.anyTransportationLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyTransportationLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Transportation limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with transportation limitations: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.transportationLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>)
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Activites/Social">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_SOCIAL_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.IADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      IADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_SOCIAL_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <Divider><Tag severity="warning">Social Activities</Tag></Divider>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.activityLevel })}>Resident social activities stage:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="activityLevel"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={INSTRUMENTAL_ACTIVITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select an option"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('activityLevel')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="activityDetails" className={classNames({ 'p-error': errors.activityDetails })}>
                              Add social activity details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="activityDetails" control={control}
                              rules={{ required: 'Social activity details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add social activity details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('activityDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="activityAssistanceRequired" className={classNames({ 'p-error': errors.activityAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="activityAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for social activities"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('activityAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any social activities? </strong>{sectionData?.activityLevel}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Social activity details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.activityDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with social activities: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.activityAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyReligiousActivities })}>Any religious activities?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyReligiousActivities"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyReligiousActivities')}
                        </div>

                        {
                          watchAnyReligiousActivities && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="religiousActivitiesDetails" className={classNames({ 'p-error': errors.religiousActivitiesDetails })}>
                                    Add details of religious activities:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="religiousActivitiesDetails" control={control}
                                    rules={{ required: 'Religious activities details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add religious activities details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('religiousActivitiesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="religiousActivitiesAssistanceRequired" className={classNames({ 'p-error': errors.religiousActivitiesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="religiousActivitiesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for religious activities"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('religiousActivitiesAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any religious activities: </strong>{sectionData?.anyReligiousActivities ? YES : NO}</div>
                        {
                          sectionData?.anyReligiousActivities && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Religious activities: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.religiousActivitiesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with religious activities: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.religiousActivitiesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySocialCulturalActivities })}>Any social or cultural activities?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySocialCulturalActivities"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySocialCulturalActivities')}
                        </div>

                        {
                          watchAnySocialCulturalActivities && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="socialCulturalActivitiesDetails" className={classNames({ 'p-error': errors.socialCulturalActivitiesDetails })}>
                                    Add details of social or cultural activities:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="socialCulturalActivitiesDetails" control={control}
                                    rules={{ required: 'Social or cultural activities details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add social or cultural activities details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('socialCulturalActivitiesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="socialCulturalActivitiesAssistanceRequired" className={classNames({ 'p-error': errors.socialCulturalActivitiesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="socialCulturalActivitiesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for social or cultural activities"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('socialCulturalActivitiesAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any social or cultural activities: </strong>{sectionData?.anySocialCulturalActivities ? YES : NO}</div>
                        {
                          sectionData?.anySocialCulturalActivities && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Social or cultural activities: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.socialCulturalActivitiesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with social or cultural activities: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.socialCulturalActivitiesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFamilyFriendsRelationships })}>Any family, friends, or relationships?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFamilyFriendsRelationships"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFamilyFriendsRelationships')}
                        </div>

                        {
                          watchAnyFamilyFriendsRelationships && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="familyFriendsRelationshipsDetails" className={classNames({ 'p-error': errors.familyFriendsRelationshipsDetails })}>
                                    Add details of family, friends, or relationships:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="familyFriendsRelationshipsDetails" control={control}
                                    rules={{ required: 'Family, friends, or relationships details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add family, friends, or relationships details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('familyFriendsRelationshipsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="familyFriendsRelationshipsAssistanceRequired" className={classNames({ 'p-error': errors.familyFriendsRelationshipsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="familyFriendsRelationshipsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for family, friends, or relationships"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('familyFriendsRelationshipsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any family, friends, or relationships: </strong>{sectionData?.anyFamilyFriendsRelationships ? YES : NO}</div>
                        {
                          sectionData?.anyFamilyFriendsRelationships && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Family, friends, or relationships: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.familyFriendsRelationshipsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with family, friends, or relationships: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.familyFriendsRelationshipsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyEmploymentSupport })}>Any employment support?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyEmploymentSupport"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyEmploymentSupport')}
                        </div>

                        {
                          watchAnyEmploymentSupport && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="employmentSupportDetails" className={classNames({ 'p-error': errors.employmentSupportDetails })}>
                                    Add details of employment support:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="employmentSupportDetails" control={control}
                                    rules={{ required: 'Employment support details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add employment support details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('employmentSupportDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="employmentSupportAssistanceRequired" className={classNames({ 'p-error': errors.employmentSupportAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="employmentSupportAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for employment support"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('employmentSupportAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any employment support: </strong>{sectionData?.anyEmploymentSupport ? YES : NO}</div>
                        {
                          sectionData?.anyEmploymentSupport && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Employment support: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.employmentSupportDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with employment support: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.employmentSupportAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyClubsGroups })}>Any clubs or groups or day health?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyClubsGroups"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyClubsGroups')}
                        </div>

                        {
                          watchAnyClubsGroups && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="clubsGroupsDetails" className={classNames({ 'p-error': errors.clubsGroupsDetails })}>
                                    Add details of clubs or groups or day health:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="clubsGroupsDetails" control={control}
                                    rules={{ required: 'Clubs or groups or day health details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add clubs or groups or day health details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('clubsGroupsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="clubsGroupsAssistanceRequired" className={classNames({ 'p-error': errors.clubsGroupsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="clubsGroupsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for clubs or groups or day health"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('clubsGroupsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any clubs, groups, or day health: </strong>{sectionData?.anyClubsGroups ? YES : NO}</div>
                        {
                          sectionData?.anyClubsGroups && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Clubs, groups, or day health: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.clubsGroupsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with clubs, groups, or day health: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.clubsGroupsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySpecialArrangements })}>Any special arrangements?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySpecialArrangements"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySpecialArrangements')}
                        </div>

                        {
                          watchAnySpecialArrangements && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialArrangementsDetails" className={classNames({ 'p-error': errors.specialArrangementsDetails })}>
                                    Add details of special arrangements:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialArrangementsDetails" control={control}
                                    rules={{ required: 'Special arrangements details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add special arrangements details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialArrangementsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialArrangementsAssistanceRequired" className={classNames({ 'p-error': errors.specialArrangementsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialArrangementsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for special arrangements"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialArrangementsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special arrangements: </strong>{sectionData?.anySpecialArrangements ? YES : NO}</div>
                        {
                          sectionData?.anySpecialArrangements && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Special arrangements: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialArrangementsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with special arrangements: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialArrangementsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyParticipationIssues })}>Any participation issues?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyParticipationIssues"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyParticipationIssues')}
                        </div>

                        {
                          watchAnyParticipationIssues && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="participationIssuesDetails" className={classNames({ 'p-error': errors.participationIssuesDetails })}>
                                    Add details of participation issues:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="participationIssuesDetails" control={control}
                                    rules={{ required: 'Participation issues details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add participation issues details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('participationIssuesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="participationIssuesAssistanceRequired" className={classNames({ 'p-error': errors.participationIssuesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="participationIssuesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for participation issues"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('participationIssuesAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any participation issues: </strong>{sectionData?.anyParticipationIssues ? YES : NO}</div>
                        {
                          sectionData?.anyParticipationIssues && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Participation issues: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.participationIssuesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with participation issues: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.participationIssuesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="emergencyNumbers" className={classNames({ 'p-error': errors.emergencyNumbers })}>
                              Emergency contact numbers: (See face sheet in resident binder)
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="emergencyNumbers" control={control}
                              rules={{ required: 'Emergency contact numbers are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add emergency contact numbers"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('emergencyNumbers')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Emergency contact numbers: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.emergencyNumbers || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.activitiesAtGlance })}>Activities at a Glance:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="activitiesAtGlance"
                              control={control}
                              render={({ field }) => (
                                <MultiSelect placeholder="Select all that apply ..." options={CARE_PLAN_ACTIVITIES_AT_A_GLANCE} optionLabel="name"
                                  {...field}
                                  value={field.value}
                                  maxSelectedLabels={5}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('activitiesAtGlance')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Activities at a Glance: </strong>
                          {
                            sectionData?.activitiesAtGlance && sectionData.activitiesAtGlance.length > 0 ? (
                              <ul>
                                {sectionData.activitiesAtGlance.map((activity: string) => (
                                  <li key={activity}>{activity}</li>
                                ))}
                              </ul>
                            ) : (
                              <span>{NA}</span>
                            )
                          }
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyOtherActivities })}>Any other activities?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyOtherActivities"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyOtherActivities')}
                        </div>

                        {
                          watchAnyOtherActivities && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="otherActivitiesDetails" className={classNames({ 'p-error': errors.otherActivitiesDetails })}>
                                    Add details of other activities:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="otherActivitiesDetails" control={control}
                                    rules={{ required: 'Other activities details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add other activities details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('otherActivitiesDetails')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other activities: </strong>{sectionData?.anyOtherActivities ? YES : NO}</div>
                        {
                          sectionData?.anyOtherActivities && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Other activities: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherActivitiesDetails || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="activityNarrative" className={classNames({ 'p-error': errors.activityNarrative })}>
                              Add activity narrative:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="activityNarrative" control={control}
                              rules={{ required: 'Activity narrative is required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add activity narrative"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('activityNarrative')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Activity narrative: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.activityNarrative || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Smoking">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_SMOKING_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.IADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      IADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.INSTRUMENTAL_SMOKING_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <Divider><Tag severity="warning">Smoking</Tag></Divider>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.doesSmoke })}>Resident smokes?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="doesSmoke"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('doesSmoke')}
                        </div>

                        {
                          watchDoesSmoke && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="smokingDetails" className={classNames({ 'p-error': errors.smokingDetails })}>
                                    Add smoking details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="smokingDetails" control={control}
                                    rules={{ required: 'Smoking details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add smoking details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('smokingDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="smokingAssistanceRequired" className={classNames({ 'p-error': errors.smokingAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="smokingAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for smoking"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                  <div className="flex justify-content-center">
                                    <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                      onClick={(e) => { renderSuggestedInstructionsDialog(e, 'smokingAssistanceRequired', SUGGESTED_INSTRUCTIONS_SMOKING); }} />
                                  </div>
                                </span>
                                {getFormErrorMessage('smokingAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident smokes? </strong>{sectionData?.doesSmoke ? YES : NO}</div>
                        {
                          sectionData?.doesSmoke && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Smoking details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.smokingDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with smoking: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.smokingAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySmokingSafetyConcerns })}>Any smoking safety concerns?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySmokingSafetyConcerns"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySmokingSafetyConcerns')}
                        </div>

                        {
                          watchAnySmokingSafetyConcerns && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="smokingSafetyConcernsDetails" className={classNames({ 'p-error': errors.smokingSafetyConcernsDetails })}>
                                    Add details of smoking safety concerns:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="smokingSafetyConcernsDetails" control={control}
                                    rules={{ required: 'Smoking safety concerns details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add smoking safety concerns details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('smokingSafetyConcernsDetails')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any smoking safety concerns? </strong>{sectionData?.anySmokingSafetyConcerns ? YES : NO}</div>
                        {
                          sectionData?.anySmokingSafetyConcerns && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Smoking safety concerns details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.smokingSafetyConcernsDetails || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySmokingPolicySigned })}>Any smoking policy signed?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySmokingPolicySigned"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySmokingPolicySigned')}
                        </div>

                        {
                          watchAnySmokingPolicySigned && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="smokingPolicySignedDetails" className={classNames({ 'p-error': errors.smokingPolicySignedDetails })}>
                                    Add smoking policy signed details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="smokingPolicySignedDetails" control={control}
                                    rules={{ required: 'Smoking policy signed details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add smoking policy signed details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('smokingPolicySignedDetails')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any smoking policy signed? </strong>{sectionData?.anySmokingPolicySigned ? YES : NO}</div>
                        {
                          sectionData?.anySmokingPolicySigned && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Smoking policy signed details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.smokingPolicySignedDetails || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">

                        {
                          watchDoesSmoke && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="smokingStorageDetails" className={classNames({ 'p-error': errors.smokingStorageDetails })}>
                                    Add storage details for smoking materials:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="smokingStorageDetails" control={control}
                                    rules={{ required: 'Smoking details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add storage details for smoking materials"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('smokingStorageDetails')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Storage details for smoking materials: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.smokingStorageDetails || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Case Management">
                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.receivesCaseManagement })}>Resident has a case management?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="receivesCaseManagement"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('receivesCaseManagement')}
                        </div>

                        {
                          watchReceivesCaseManagement && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="caseManagementDetails" className={classNames({ 'p-error': errors.caseManagementDetails })}>
                                    Add case management details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="caseManagementDetails" control={control}
                                    rules={{ required: 'Case management details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add case management details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('caseManagementDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="caseManagementAssistanceRequired" className={classNames({ 'p-error': errors.caseManagementAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="caseManagementAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for case management"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('caseManagementAssistanceRequired')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="caseManagerContactName" className={classNames({ 'p-error': errors.caseManagerContactName })}>
                                    Add case management contact name:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="caseManagerContactName" control={control}
                                    rules={{ required: 'Contact name is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputText placeholder="Enter case management contact name"
                                        style={{ width: '100%' }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('caseManagerContactName')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="caseManagerContactAgency" className={classNames({ 'p-error': errors.caseManagerContactAgency })}>
                                    Add case management agency name:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="caseManagerContactAgency" control={control}
                                    rules={{ required: 'Agency name is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputText placeholder="Enter case management agency name"
                                        style={{ width: '100%' }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('caseManagerContactAgency')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="caseManagerContactEmail" className={classNames({ 'p-error': errors.caseManagerContactEmail })}>
                                    Add case management contact email:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="caseManagerContactEmail" control={control}
                                    rules={{ required: 'Contact email is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputText placeholder="Enter case management contact email"
                                        style={{ width: '100%' }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('caseManagerContactEmail')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="caseManagerContactPhone" className={classNames({ 'p-error': errors.caseManagerContactPhone })}>
                                    Add case management contact phone:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="caseManagerContactPhone" control={control}
                                    rules={{ required: 'Contact phone is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputText placeholder="Enter case management contact phone"
                                        style={{ width: '100%' }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('caseManagerContactPhone')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="caseManagerContactFax" className={classNames({ 'p-error': errors.caseManagerContactFax })}>
                                    Add case management contact fax:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="caseManagerContactFax" control={control}
                                    rules={{ required: 'Contact fax is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputText placeholder="Enter case management contact fax"
                                        style={{ width: '100%' }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('caseManagerContactFax')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Are case management details included? </strong>{sectionData?.receivesCaseManagement ? YES : NO}</div>
                        {
                          sectionData?.receivesCaseManagement && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Case management details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.caseManagementDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with case management: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.caseManagementAssistanceRequired || NA)}</span>
                              </div>

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Case management agency name: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.caseManagerContactAgency || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Case management contact name: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.caseManagerContactName || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Case management contact fax: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.caseManagerContactFax || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Case management contact phone: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.caseManagerContactPhone || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Case management contact email: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.caseManagerContactEmail || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Other Issues/Concerns/Problems">
                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="additionalInformation" className={classNames({ 'p-error': errors.additionalInformation })}>
                              Add any additional information like service dog etc:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="additionalInformation" control={control}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add any additional information like service dog etc"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('additionalInformation')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Additional information: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.additionalInformation || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              {/* <AccordionTab header="NCP Review Details">
                <div className="flex-column flex-wrap ">
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="ncpReviewDetails" className={classNames({ 'p-error': errors.ncpReviewDetails })}>
                              Add NCP review details with resident:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="ncpReviewDetails" control={control}
                              rules={{ required: 'NCP review details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add NCP review details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('ncpReviewDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="ncpReviewAssistanceRequired" className={classNames({ 'p-error': errors.ncpReviewAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="ncpReviewAssistanceRequired" control={control}
                              
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('ncpReviewAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>NCP review details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.ncpReviewDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with review: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.ncpReviewAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>
              </AccordionTab> */}
            </Accordion>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default InstrumentalActivitiesSection;


